import {
	type EditorState as CodeMirrorEditorState,
	type Transaction as CodeMirrorTransaction,
} from "@codemirror/state";
import { useSelector } from "@xstate/react";
import {
	type EditorState as ProseMirrorEditorState,
	type Transaction as ProseMirrorTransaction,
} from "prosemirror-state";

import { usePageActor, useTransactionDispatched } from "~/state";

export type CreateTransactionFn = (
	state: ProseMirrorEditorState | CodeMirrorEditorState
) => ProseMirrorTransaction | CodeMirrorTransaction | undefined;
export type EaselTransaction = {
	createTransaction: CreateTransactionFn;
	shouldComputeImmediately?: boolean;
};

export const usePageEditorState = (documentId: string, pageId: string) => {
	const pageActor = usePageActor(documentId, pageId);
	const transactionDispatched = useTransactionDispatched(documentId, pageId);

	const editorState = useSelector(pageActor, (state) => state?.context.editorState);
	const markdownEditorState = useSelector(
		pageActor,
		(state) => state?.context.markdownEditorState
	);

	const applyTransaction = ({
		createTransaction,
		shouldComputeImmediately,
	}: EaselTransaction) => {
		const pageActorSnap = pageActor?.getSnapshot();
		if (!pageActorSnap) {
			console.error("No page actor snapshot");
			throw new Error("No page actor snapshot");
		}

		const editorStateSnap = pageActorSnap?.context.editorState;
		const pageIdSnap = pageActorSnap.context.pageId;

		if (!editorStateSnap) {
			console.error("No editor state", { pageIdSnap });
			throw new Error("No editor state");
		}

		const transaction = createTransaction(editorStateSnap);
		if (!transaction) {
			console.error("No transaction", { pageIdSnap });
			throw new Error("No transaction");
		}

		transactionDispatched({ pageId: pageIdSnap, transaction, timestamp: new Date() });

		if (shouldComputeImmediately) {
			pageActor?.send({ type: "page.compute" });
		}
	};

	return { editorState, applyTransaction, markdownEditorState };
};
